import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import {
  fetchPublicJobPostingDetail,
  createJobPostingDraft,
  updateJobPostingDraft,
  submitJobPostingAudit,
} from '@/service/api/job';
import { Select, DatePicker, Toast, CityCascader } from '@/components/common';
import { useAuth } from '@/hooks/useAuth';
import { CURRENCY_OPTIONS } from '@/utils/currency';

const getTomorrowString = (): string => {
  const now = new Date();
  now.setDate(now.getDate() + 1);
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const EMPLOYMENT_TYPE_OPTIONS: { label: string; value: Api.Job.EmploymentType }[] = [
  { label: '全职', value: 'FULL_TIME' },
  { label: '兼职', value: 'PART_TIME' },
  { label: '实习', value: 'INTERNSHIP' },
  { label: '项目/外包', value: 'PROJECT' },
];

const SALARY_TYPE_OPTIONS: { label: string; value: Api.Job.SalaryType }[] = [
  { label: '范围薪资', value: 'RANGE' },
  { label: '固定薪资', value: 'FIXED' },
  { label: '面议', value: 'NEGOTIABLE' },
];

const SALARY_PERIOD_OPTIONS: { label: string; value: Api.Job.SalaryPeriod }[] = [
  { label: '月', value: 'MONTHLY' },
  { label: '年', value: 'YEARLY' },
  { label: '日', value: 'DAILY' },
  { label: '小时', value: 'HOURLY' },
  { label: '项目', value: 'PROJECT' },
];

export default function PostJobPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');

  const { user, isEnterprise } = useAuth();
  const isEnterpriseVerified = isEnterprise && user?.verified;
  const { categories, fetchCategories, loading: categoriesLoading } = useBusinessCategoryStore();

  const [formData, setFormData] = useState({
    title: '',
    employmentType: 'FULL_TIME' as Api.Job.EmploymentType,
    categoryCodes: [] as string[],
    salaryType: 'RANGE' as Api.Job.SalaryType,
    salaryPeriod: 'MONTHLY' as Api.Job.SalaryPeriod,
    salaryMin: '',
    salaryMax: '',
    salaryCurrency: 'CNY',
    geoCityId: undefined as number | undefined,
    deadlineAt: '',
    jobDescription: '',
    jobRequirements: '',
    benefits: '',
    skillRequirements: '',
  });

  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // 当为编辑模式时，从 API 读取岗位数据
  useEffect(() => {
    if (!editId) return;
    setLoadingDetail(true);
    fetchPublicJobPostingDetail(editId)
      .then((res) => {
        if (!res) return;

        let deadlineStr = '';
        if (res.deadline_at) {
          const d = new Date(res.deadline_at);
          if (!isNaN(d.getTime())) {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            deadlineStr = `${y}-${m}-${day}`;
          }
        }

        const categoryCodes = Array.isArray(res.category_codes)
          ? res.category_codes
          : res.category_codes
          ? [res.category_codes as unknown as string]
          : [];

        setFormData({
          title: res.title || '',
          employmentType: (res.employment_type as Api.Job.EmploymentType) || 'FULL_TIME',
          categoryCodes,
          salaryType: (res.salary_type as Api.Job.SalaryType) || 'RANGE',
          salaryPeriod: (res.salary_period as Api.Job.SalaryPeriod) || 'MONTHLY',
          salaryMin: res.salary_min ? String(res.salary_min) : '',
          salaryMax: res.salary_max ? String(res.salary_max) : '',
          salaryCurrency: res.salary_currency || 'CNY',
          geoCityId: res.geo_city_id,
          deadlineAt: deadlineStr,
          jobDescription: res.job_description || '',
          jobRequirements: res.job_requirements || '',
          benefits: res.benefits || '',
          skillRequirements: res.skill_requirements || '',
        });
      })
      .catch((err) => {
        console.error('加载招聘岗位详情失败:', err);
        showToast('加载招聘岗位数据失败，请重试', 'error');
      })
      .finally(() => {
        setLoadingDetail(false);
      });
  }, [editId]);

  const handleSalaryMinBlur = () => {
    if (!formData.salaryMin.trim() || !formData.salaryMax.trim()) return;
    const minVal = parseFloat(formData.salaryMin);
    const maxVal = parseFloat(formData.salaryMax);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      setFormData((prev) => ({ ...prev, salaryMax: prev.salaryMin }));
    }
  };

  const handleSalaryMaxBlur = () => {
    if (!formData.salaryMin.trim() || !formData.salaryMax.trim()) return;
    const minVal = parseFloat(formData.salaryMin);
    const maxVal = parseFloat(formData.salaryMax);
    if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
      setFormData((prev) => ({ ...prev, salaryMax: prev.salaryMin }));
    }
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      showToast('请输入岗位名称', 'error');
      return false;
    }
    if (!formData.categoryCodes || formData.categoryCodes.length === 0) {
      showToast('请至少选择一个岗位分类', 'error');
      return false;
    }
    if (formData.salaryType === 'RANGE') {
      if (!formData.salaryMin.trim()) {
        showToast('请输入薪资下限', 'error');
        return false;
      }
      if (!formData.salaryMax.trim()) {
        showToast('请输入薪资上限', 'error');
        return false;
      }
      const minVal = parseFloat(formData.salaryMin);
      const maxVal = parseFloat(formData.salaryMax);
      if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
        showToast('薪资上限不能低于薪资下限', 'error');
        return false;
      }
    } else if (formData.salaryType === 'FIXED') {
      if (!formData.salaryMin.trim()) {
        showToast('请输入固定薪资金额', 'error');
        return false;
      }
    }
    if (!formData.jobDescription.trim()) {
      showToast('请输入岗位描述', 'error');
      return false;
    }
    if (!formData.jobRequirements.trim()) {
      showToast('请输入岗位要求', 'error');
      return false;
    }
    return true;
  };

  const buildPayload = (): Api.Job.JobRequest => {
    let deadlineAtStr: string | undefined;
    if (formData.deadlineAt) {
      const trimmed = formData.deadlineAt.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        deadlineAtStr = trimmed;
      } else {
        const d = new Date(trimmed);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          deadlineAtStr = `${y}-${m}-${day}`;
        }
      }
    }

    return {
      title: formData.title.trim(),
      employment_type: formData.employmentType,
      category_codes: formData.categoryCodes,
      salary_type: formData.salaryType,
      salary_period: formData.salaryPeriod,
      salary_currency: formData.salaryCurrency || 'CNY',
      salary_min: formData.salaryMin.trim() || undefined,
      salary_max: formData.salaryType === 'RANGE' ? formData.salaryMax.trim() || undefined : undefined,
      geo_city_id: formData.geoCityId,
      deadline_at: deadlineAtStr,
      job_description: formData.jobDescription.trim(),
      job_requirements: formData.jobRequirements.trim(),
      benefits: formData.benefits.trim() || undefined,
      skill_requirements: formData.skillRequirements.trim() || undefined,
    };
  };

  const handleSaveDraft = async () => {
    if (!validateForm()) return;

    try {
      setIsSavingDraft(true);
      const payload = buildPayload();
      if (editId) {
        await updateJobPostingDraft(editId, payload);
      } else {
        await createJobPostingDraft(payload);
      }
      showToast(editId ? '招聘岗位更新成功！正在跳转...' : '保存招聘草稿成功！正在跳转...', 'success');
      setTimeout(() => {
        navigate('/my/job-postings');
      }, 1200);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '保存招聘草稿失败，请稍后重试';
      showToast(msg, 'error');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);
      const payload = buildPayload();
      let targetId = editId;
      if (editId) {
        await updateJobPostingDraft(editId, payload);
      } else {
        const draftRes = await createJobPostingDraft(payload);
        targetId = draftRes?.id || null;
      }

      if (targetId) {
        await submitJobPostingAudit(targetId);
      }
      showToast('招聘岗位已成功提交审核！正在跳转...', 'success');
      setTimeout(() => {
        navigate('/my/job-postings');
      }, 1200);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '发布招聘失败，请稍后重试';
      showToast(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <div className="pt-28 sm:pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <nav className="flex items-center gap-2 text-xs text-slate-500 mb-6 pl-1 sm:pl-2">
            <Link to="/" className="hover:text-slate-800 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/my/job-postings" className="hover:text-slate-800 transition-colors cursor-pointer">
              招聘管理
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-slate-800 font-medium">{editId ? '编辑招聘岗位' : '发布招聘岗位'}</span>
          </nav>

          <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-slate-100">
            {!isEnterprise ? (
              <div className="py-16 text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-2xl bg-amber-100/80 text-amber-600 flex items-center justify-center mx-auto mb-4 text-2xl shadow-sm">
                  <i className="ri-building-4-line"></i>
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-2">发布招聘功能仅面向企业账号开放</h2>
                <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                  只有企业账号才可以发布招聘岗位招募 AIGC 人才。个人账号如需发布招聘，请先完成企业认证转为企业账号。
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/talents"
                    className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50 transition-all"
                  >
                    返回人才广场
                  </Link>
                  <Link
                    to="/certification"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all shadow-md shadow-primary-500/20"
                  >
                    <i className="ri-shield-check-line text-sm"></i>
                    前往企业认证
                  </Link>
                </div>
              </div>
            ) : !isEnterpriseVerified ? (
              <div className="py-16 text-center max-w-md mx-auto">
                <div className="w-16 h-16 rounded-2xl bg-amber-100/80 text-amber-600 flex items-center justify-center mx-auto mb-4 text-2xl shadow-sm">
                  <i className="ri-building-4-line"></i>
                </div>
                <h2 className="text-base font-bold text-slate-900 mb-2">需要完成企业认证</h2>
                <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                  发布招聘功能仅面向通过认证的企业账号开放。完成企业认证后，即可在线发布岗位招揽人才。
                </p>
                <div className="flex items-center justify-center gap-3">
                  <Link
                    to="/talents"
                    className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-xs font-medium hover:bg-slate-50 transition-all"
                  >
                    返回人才广场
                  </Link>
                  <Link
                    to="/certification"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all shadow-md shadow-primary-500/20"
                  >
                    <i className="ri-shield-check-line text-sm"></i>
                    前往企业认证
                  </Link>
                </div>
              </div>
            ) : loadingDetail ? (
              <div className="py-16 text-center">
                <div className="inline-block w-8 h-8 border-3 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs text-slate-400 mt-3">正在加载招聘岗位原数据...</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between pb-6 mb-6 border-b border-slate-100">
                  <div>
                    <h1 className="font-bold text-lg sm:text-xl text-slate-900">
                      {editId ? '编辑招聘岗位' : '发布招聘岗位'}
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">
                      发布招聘岗位招募 AI 视频、编剧、特效及全流程影视人才
                    </p>
                  </div>
                  <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium border border-emerald-200/60">
                    企业已认证
                  </span>
                </div>

                <div className="space-y-6">
                  {/* 岗位名称与就业类型 */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">
                        岗位名称 <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="如：Sora/Runway AI视频分镜导演"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">
                        就业类型 <span className="text-red-500 ml-0.5">*</span>
                      </label>
                      <Select
                        value={formData.employmentType}
                        onChange={(val) => setFormData({ ...formData, employmentType: val as Api.Job.EmploymentType })}
                        options={EMPLOYMENT_TYPE_OPTIONS}
                        placeholder="请选择类型"
                      />
                    </div>
                  </div>

                  {/* 岗位分类 */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-medium text-slate-700">
                        岗位分类 <span className="text-red-500 ml-0.5">*</span>
                        <span className="text-slate-400 font-normal ml-1">（可多选）</span>
                      </label>
                      {formData.categoryCodes.length > 0 && (
                        <span className="text-xs text-primary-600 font-medium">
                          已选择 {formData.categoryCodes.length} 项
                        </span>
                      )}
                    </div>
                    {categoriesLoading ? (
                      <div className="flex items-center gap-2 py-3 text-xs text-slate-400">
                        <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                        分类加载中...
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                        {categories.map((cat) => {
                          const isSelected = formData.categoryCodes.includes(cat.code);
                          return (
                            <button
                              key={cat.code}
                              type="button"
                              onClick={() => {
                                const newCodes = isSelected
                                  ? formData.categoryCodes.filter((c) => c !== cat.code)
                                  : [...formData.categoryCodes, cat.code];
                                setFormData({ ...formData, categoryCodes: newCodes });
                              }}
                              className={`px-3.5 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 ${
                                isSelected
                                  ? 'bg-primary-50 border border-primary-300 text-primary-600 shadow-2xs font-semibold'
                                  : 'bg-white text-slate-700 border border-slate-200 hover:border-primary-300/60 hover:bg-primary-50/20'
                              }`}
                            >
                              {isSelected ? (
                                <i className="ri-checkbox-circle-fill text-primary-600 text-sm"></i>
                              ) : (
                                <i className="ri-checkbox-blank-circle-line text-slate-400 text-sm"></i>
                              )}
                              {cat.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 工作城市 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      工作城市
                    </label>
                    <CityCascader
                      value={formData.geoCityId}
                      onChange={(cityId) => setFormData({ ...formData, geoCityId: cityId })}
                      placeholder="选择工作城市"
                    />
                  </div>

                  {/* 薪资结构 */}
                  <div className="p-4 rounded-xl bg-slate-50 border border-slate-200/80 space-y-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1.5">
                          薪资类型 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <Select
                          value={formData.salaryType}
                          onChange={(val) => setFormData({ ...formData, salaryType: val as Api.Job.SalaryType })}
                          options={SALARY_TYPE_OPTIONS}
                          placeholder="选择薪资类型"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1.5">
                          结算周期 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <Select
                          value={formData.salaryPeriod}
                          onChange={(val) => setFormData({ ...formData, salaryPeriod: val as Api.Job.SalaryPeriod })}
                          options={SALARY_PERIOD_OPTIONS}
                          placeholder="选择周期"
                        />
                      </div>
                      <div className="col-span-2 sm:col-span-1">
                        <label className="block text-xs font-medium text-slate-700 mb-1.5">
                          结算币种
                        </label>
                        <Select
                          value={formData.salaryCurrency}
                          onChange={(val) => setFormData({ ...formData, salaryCurrency: String(val) })}
                          options={CURRENCY_OPTIONS}
                          placeholder="币种"
                        />
                      </div>
                    </div>

                    {formData.salaryType === 'RANGE' && (
                      <div className="grid grid-cols-2 gap-4 pt-1">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1.5">
                            薪资下限 <span className="text-red-500 ml-0.5">*</span>
                          </label>
                          <input
                            type="number"
                            value={formData.salaryMin}
                            onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value })}
                            onBlur={handleSalaryMinBlur}
                            placeholder="如 12000"
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-primary-500 transition-all"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1.5">
                            薪资上限 <span className="text-red-500 ml-0.5">*</span>
                          </label>
                          <input
                            type="number"
                            value={formData.salaryMax}
                            onChange={(e) => setFormData({ ...formData, salaryMax: e.target.value })}
                            onBlur={handleSalaryMaxBlur}
                            placeholder="如 20000"
                            className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-primary-500 transition-all"
                          />
                        </div>
                      </div>
                    )}

                    {formData.salaryType === 'FIXED' && (
                      <div className="pt-1">
                        <label className="block text-xs font-medium text-slate-700 mb-1.5">
                          固定薪资金额 <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <input
                          type="number"
                          value={formData.salaryMin}
                          onChange={(e) => setFormData({ ...formData, salaryMin: e.target.value, salaryMax: e.target.value })}
                          placeholder="如 15000"
                          className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-primary-500 transition-all"
                        />
                      </div>
                    )}

                    {formData.salaryType === 'NEGOTIABLE' && (
                      <div className="p-3 rounded-lg bg-amber-50/60 border border-amber-200/60 text-xs text-amber-800 flex items-center gap-2">
                        <i className="ri-information-line text-amber-600 text-base"></i>
                        薪资面议，具体薪酬将在后续面试与沟通中与求职者协商确定。
                      </div>
                    )}
                  </div>

                  {/* 截止日期 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      招聘截止日期
                    </label>
                    <DatePicker
                      value={formData.deadlineAt}
                      minDate={getTomorrowString()}
                      onChange={(date) => setFormData({ ...formData, deadlineAt: date })}
                      placeholder="选择招聘截止日期 (可选)"
                    />
                  </div>

                  {/* 岗位描述 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      岗位描述 <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <textarea
                      value={formData.jobDescription}
                      onChange={(e) => setFormData({ ...formData, jobDescription: e.target.value })}
                      placeholder="请详细描述该岗位的核心职责、项目背景及团队合作方式..."
                      rows={5}
                      maxLength={1000}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all resize-none"
                    ></textarea>
                    <p className="text-xs text-slate-400 mt-1 text-right">{formData.jobDescription.length}/1000</p>
                  </div>

                  {/* 岗位要求 */}
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      岗位要求 <span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <textarea
                      value={formData.jobRequirements}
                      onChange={(e) => setFormData({ ...formData, jobRequirements: e.target.value })}
                      placeholder="请列出应聘要求，如：专业背景、工作年限、AIGC工具使用经验等..."
                      rows={4}
                      maxLength={800}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10 transition-all resize-none"
                    ></textarea>
                  </div>

                  {/* 技能要求 & 福利待遇 */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">
                        技能要求关键词
                      </label>
                      <input
                        type="text"
                        value={formData.skillRequirements}
                        onChange={(e) => setFormData({ ...formData, skillRequirements: e.target.value })}
                        placeholder="如：Sora, Midjourney, ComfyUI, 剪映"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-primary-500 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">
                        福利待遇
                      </label>
                      <input
                        type="text"
                        value={formData.benefits}
                        onChange={(e) => setFormData({ ...formData, benefits: e.target.value })}
                        placeholder="如：五险一金、双休、远程办公、绩效奖金"
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 outline-none focus:border-primary-500 transition-all"
                      />
                    </div>
                  </div>

                  {/* 操作按钮区 */}
                  <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={isSubmitting || isSavingDraft}
                      className="flex-1 py-3 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold transition-all cursor-pointer shadow-md shadow-primary-500/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting && <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>}
                      发布招聘 (提交审核)
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={isSubmitting || isSavingDraft}
                      className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSavingDraft && <div className="w-4 h-4 rounded-full border-2 border-slate-600/20 border-t-slate-600 animate-spin"></div>}
                      保存为草稿
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
